import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertTriangle, TrendingUp, TrendingDown, Target, DollarSign, BarChart3 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useOpsDashboard } from '@/context/OpsDashboardContext';

interface OverallHealth {
  target_budget: number;
  assigned_revenue: number;
  percent_to_target: number;
  remaining_to_fill: number;
  overall_utilization: number;
  utilization_headroom: number;
}

interface GapByVertical {
  vertical: string;
  supply_reach: number;
  demand_impressions: number;
  gap: number;
  status: 'green' | 'yellow' | 'red';
  utilization: number;
  fill_opportunity: number;
}

interface FloorAnalysis {
  total_projects: number;
  clearable_count: number;
  clearable_percent: number;
  constrained_count: number;
  constrained_percent: number;
  revenue_blocked: number;
  avg_floor_vs_avg_brand_ecpm: number;
}

interface CompensationRecovery {
  at_risk_shows: number;
  at_risk_revenue: number;
  available_fill_capacity: number;
  fill_recovery_potential: number;
  recovery_ratio: number;
}

interface UtilizationAnalysisResponse {
  overall_health: OverallHealth;
  gap_by_vertical: GapByVertical[];
  floor_analysis: FloorAnalysis;
  compensation_recovery: CompensationRecovery;
}

const UtilizationAnalysisPage: React.FC = () => {
  const [data, setData] = useState<UtilizationAnalysisResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [targetBudget, setTargetBudget] = useState<number | null>(null); // Auto-calculated by backend
  const { toast } = useToast();
  const { refreshTriggers } = useOpsDashboard();

  const fetchUtilizationData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      // Only add target_budget parameter if it's explicitly set by user
      if (targetBudget !== null) {
        params.append('target_budget', targetBudget.toString());
      }

      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/v1/ops-dashboard/utilization?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch utilization data');
      }

      const result = await response.json();
      setData(result);
      
      // Update targetBudget state with the value used by backend (auto-calculated or provided)
      if (targetBudget === null && result.overall_health?.target_budget) {
        setTargetBudget(result.overall_health.target_budget);
      }
    } catch (error) {
      console.error('Error fetching utilization data:', error);
      toast({
        title: "Error",
        description: "Failed to fetch utilization data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUtilizationData();
  }, [targetBudget]);

  // Listen for refresh triggers from the context
  useEffect(() => {
    if (refreshTriggers.utilization > 0) {
      fetchUtilizationData();
    }
  }, [refreshTriggers.utilization]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'green': return 'bg-green-100 text-green-800';
      case 'yellow': return 'bg-yellow-100 text-yellow-800';
      case 'red': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'green': return <TrendingUp className="h-4 w-4 text-green-600" />;
      case 'yellow': return <BarChart3 className="h-4 w-4 text-yellow-600" />;
      case 'red': return <TrendingDown className="h-4 w-4 text-red-600" />;
      default: return <BarChart3 className="h-4 w-4 text-gray-600" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No utilization data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Utilization & Gap Analysis</h1>
          <p className="text-gray-600">Revenue tracking, supply-demand gaps, and recovery analysis</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Target Budget:</label>
            <Input
              type="number"
              value={targetBudget}
              onChange={(e) => setTargetBudget(Number(e.target.value))}
              className="w-32"
            />
          </div>
          <Button variant="outline" size="sm" onClick={fetchUtilizationData}>
            Refresh
          </Button>
        </div>
      </div>

      {/* Overall Health */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Overall Health
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600">Progress to Target</span>
                <span className="text-sm font-bold">
                  {data.overall_health.percent_to_target.toFixed(1)}%
                </span>
              </div>
              <Progress value={data.overall_health.percent_to_target} className="h-3" />
              <div className="flex justify-between text-xs text-gray-500">
                <span>{formatCurrency(data.overall_health.assigned_revenue)}</span>
                <span>{formatCurrency(data.overall_health.target_budget)}</span>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <div className="text-2xl font-bold text-green-600">
                  {formatCurrency(data.overall_health.assigned_revenue)}
                </div>
                <div className="text-sm text-gray-600">Assigned Revenue</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-red-600">
                  {formatCurrency(data.overall_health.remaining_to_fill)}
                </div>
                <div className="text-sm text-gray-600">Remaining to Fill</div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <div className="text-2xl font-bold text-blue-600">
                  {data.overall_health.overall_utilization.toFixed(1)}%
                </div>
                <div className="text-sm text-gray-600">Overall Utilization</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-600">
                  {data.overall_health.utilization_headroom.toFixed(1)}%
                </div>
                <div className="text-sm text-gray-600">Utilization Headroom</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Gap by Vertical */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Gap by Vertical
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vertical</TableHead>
                  <TableHead>Supply (Reach)</TableHead>
                  <TableHead>Demand (Impressions)</TableHead>
                  <TableHead>Gap</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Utilization</TableHead>
                  <TableHead>Fill Opportunity</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.gap_by_vertical.length > 0 ? (
                  data.gap_by_vertical.map((vertical) => (
                    <TableRow key={vertical.vertical}>
                      <TableCell className="font-medium">{vertical.vertical}</TableCell>
                      <TableCell>{formatNumber(vertical.supply_reach)}</TableCell>
                      <TableCell>{formatNumber(vertical.demand_impressions)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {vertical.gap > 0 ? (
                            <TrendingUp className="h-4 w-4 text-red-600" />
                          ) : (
                            <TrendingDown className="h-4 w-4 text-green-600" />
                          )}
                          <span className={vertical.gap > 0 ? 'text-red-600' : 'text-green-600'}>
                            {vertical.gap > 0 ? '+' : ''}{formatNumber(Math.abs(vertical.gap))}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(vertical.status)}>
                          <div className="flex items-center gap-1">
                            {getStatusIcon(vertical.status)}
                            {vertical.status}
                          </div>
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Progress value={Math.min(vertical.utilization, 100)} className="w-16 h-2" />
                          <span className="text-sm">{vertical.utilization.toFixed(1)}%</span>
                        </div>
                      </TableCell>
                      <TableCell>{formatNumber(vertical.fill_opportunity)}K</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                      No vertical data available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Floor Analysis & Compensation Recovery */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Floor Analysis */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Floor Analysis
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold">{data.floor_analysis.total_projects}</div>
                <div className="text-sm text-gray-600">Total Projects</div>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-red-600">
                  {formatCurrency(data.floor_analysis.revenue_blocked)}
                </div>
                <div className="text-sm text-gray-600">Revenue Blocked</div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Clearable Projects</span>
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-100 text-green-800">
                    {data.floor_analysis.clearable_count}
                  </Badge>
                  <span className="text-sm text-gray-600">
                    ({data.floor_analysis.clearable_percent.toFixed(1)}%)
                  </span>
                </div>
              </div>
              <Progress value={data.floor_analysis.clearable_percent} className="h-2" />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Constrained Projects</span>
                <div className="flex items-center gap-2">
                  <Badge className="bg-red-100 text-red-800">
                    {data.floor_analysis.constrained_count}
                  </Badge>
                  <span className="text-sm text-gray-600">
                    ({data.floor_analysis.constrained_percent.toFixed(1)}%)
                  </span>
                </div>
              </div>
              <Progress value={data.floor_analysis.constrained_percent} className="h-2" />
            </div>

            <div className="pt-4 border-t">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Avg Floor vs Avg Brand eCPM</span>
                <span className={`text-sm font-bold ${
                  data.floor_analysis.avg_floor_vs_avg_brand_ecpm > 0 ? 'text-red-600' : 'text-green-600'
                }`}>
                  {data.floor_analysis.avg_floor_vs_avg_brand_ecpm > 0 ? '+' : ''}
                  {formatCurrency(data.floor_analysis.avg_floor_vs_avg_brand_ecpm)}
                </span>
              </div>
              {data.floor_analysis.avg_floor_vs_avg_brand_ecpm > 0 && (
                <div className="text-xs text-red-600 mt-1">
                  Creators pricing above market
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Compensation/Recovery */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Compensation / Recovery
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-yellow-50 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">
                  {data.compensation_recovery.at_risk_shows}
                </div>
                <div className="text-sm text-gray-600">At-Risk Shows</div>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <div className="text-2xl font-bold text-red-600">
                  {formatCurrency(data.compensation_recovery.at_risk_revenue)}
                </div>
                <div className="text-sm text-gray-600">At-Risk Revenue</div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Available Fill Capacity</span>
                <Badge variant="outline">
                  {data.compensation_recovery.available_fill_capacity} slots
                </Badge>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Fill Recovery Potential</span>
                <span className="text-sm font-bold text-green-600">
                  {formatCurrency(data.compensation_recovery.fill_recovery_potential)}
                </span>
              </div>
            </div>

            <div className="pt-4 border-t">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Recovery Ratio</span>
                <div className="flex items-center gap-2">
                  <span className={`text-lg font-bold ${
                    data.compensation_recovery.recovery_ratio >= 1.0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {data.compensation_recovery.recovery_ratio.toFixed(2)}x
                  </span>
                  {data.compensation_recovery.recovery_ratio >= 1.0 ? (
                    <Badge className="bg-green-100 text-green-800">Recoverable</Badge>
                  ) : (
                    <Badge className="bg-red-100 text-red-800">Gap</Badge>
                  )}
                </div>
              </div>
              
              <div className="mt-2">
                <Progress 
                  value={Math.min(data.compensation_recovery.recovery_ratio * 100, 100)} 
                  className="h-3" 
                />
              </div>
              
              <div className="text-xs text-gray-600 mt-2">
                {data.compensation_recovery.recovery_ratio >= 1.0 
                  ? 'At-risk revenue can be recovered through increased fill'
                  : 'Additional capacity needed to recover at-risk revenue'
                }
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Key Insights */}
      <Card>
        <CardHeader>
          <CardTitle>Key Insights</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Target className="h-5 w-5 text-blue-600" />
                <span className="font-medium text-blue-900">Revenue Target</span>
              </div>
              <p className="text-sm text-blue-800">
                {data.overall_health.percent_to_target >= 75 
                  ? 'On track to meet revenue target'
                  : data.overall_health.percent_to_target >= 50
                  ? 'Moderate progress toward target'
                  : 'Significant gap to revenue target'
                }
              </p>
            </div>

            <div className="p-4 bg-green-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="h-5 w-5 text-green-600" />
                <span className="font-medium text-green-900">Supply Health</span>
              </div>
              <p className="text-sm text-green-800">
                {data.floor_analysis.clearable_percent >= 70
                  ? 'Strong clearable inventory'
                  : data.floor_analysis.clearable_percent >= 50
                  ? 'Moderate clearable inventory'
                  : 'Limited clearable inventory'
                }
              </p>
            </div>

            <div className="p-4 bg-yellow-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-5 w-5 text-yellow-600" />
                <span className="font-medium text-yellow-900">Risk Assessment</span>
              </div>
              <p className="text-sm text-yellow-800">
                {data.compensation_recovery.recovery_ratio >= 1.0
                  ? 'Low risk - can recover from show losses'
                  : 'High risk - limited recovery capacity'
                }
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default UtilizationAnalysisPage;